'use client';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller, ControllerRenderProps } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  InputSelect,
  Label,
} from '@features/ui';
import { EditIcon } from '@features/ui/icons';
import { useTranslation } from 'react-i18next';
import {
  accountTypes,
  dataFeeds,
  paymentCheckboxData,
} from '@pages/user/checkout/checkoutData.ts';
import { cn, convertCurrencyToShortForm, formatCurrency } from '@lib/utils.ts';
import { compact, sumBy } from 'lodash';
import { Link } from 'react-router-dom';
import {
  paymentFormSchema,
  PaymentFormValues,
  PaymentGateway,
  TOnboardingAccount,
  TOnboardingPayment,
  TOption,
} from '@src/types';
import { validateCoupon } from '@src/services';
import isNil from 'lodash/isNil';
import { CheckIcon, XIcon } from 'lucide-react';
import { dateSelectData } from '@features/admin/productsAddons/productsAddonsData.ts';

interface ReviewCheckoutFormProps {
  payment: TOnboardingPayment | null;
  platform: string;
  account: TOnboardingAccount | null;
  countries: TOption[];
  states: TOption[];
  onBack: () => void;
  isRenew?: boolean;
  isCreatePaAccount?: boolean;
  onSubmit: (data: PaymentFormValues) => Promise<void>;
}
export const ReviewCheckoutForm: FC<ReviewCheckoutFormProps> = ({
  payment,
  platform,
  account,
  countries,
  states,
  onBack,
  isRenew,
  isCreatePaAccount,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isValidCoupons, setIsValidCoupons] = useState<boolean>();

  // Initialize the form
  const {
    control,
    handleSubmit,
    resetField,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PaymentFormValues>({
    resolver: yupResolver(paymentFormSchema),
  });

  const country = watch('country');

  const handleFormSubmit = useCallback(
    async (data: PaymentFormValues) => {
      setIsLoading(true);
      try {
        await onSubmit(data);
      } finally {
        setIsLoading(false);
      }
    },
    [onSubmit]
  );

  useEffect(() => {
    if (payment) {
      const values = {
        street: payment.billing_info?.street,
        city: payment.billing_info?.city,
        country: payment.billing_info?.country,
        state: payment.billing_info?.state,
        zip_code: payment.billing_info?.zip_code,
        phone_number: payment.billing_info?.phone_number,
        first_name: payment.billing_info?.first_name,
        last_name: payment.billing_info?.last_name,
        coupon_code: payment.coupon_code,
        referral_source: payment.referral_source,
        payment_type: payment.payment_type,
      };

      reset(values || {});
    }
  }, [payment, reset]);

  const accountType = useMemo(
    () =>
      accountTypes.find((item) => item.value === account?.type)?.label ?? '',
    [account?.type]
  );
  const selectedAddons = useMemo(
    () =>
      compact(
        account?.selectedAddonIds?.map((id) => {
          const findAddon = account?.addons?.find((item) => item.id === id);

          if (findAddon) {
            return findAddon;
          }

          return null;
        }) ?? []
      ),
    [account?.addons, account?.selectedAddonIds]
  );
  const taxes = useMemo(() => 0, []);
  const addonPrice = useMemo(
    () => sumBy(selectedAddons, 'price'),
    [selectedAddons]
  );
  const accountPrice = useMemo(
    () => account?.billing_plan?.first_price ?? 0,
    [account?.billing_plan?.first_price]
  );

  const firstPeriodValue = useMemo(
    () => account?.billing_plan?.first_period_value ?? 0,
    [account?.billing_plan?.first_period_value]
  );

  const firstPeriodUnit = useMemo(
    () =>
      dateSelectData.find(
        (item) => item.value === account?.billing_plan?.first_period_unit
      )?.label,
    [account?.billing_plan?.first_period_unit]
  );

  const orderDetails = useMemo(
    () => [
      {
        label: 'Trading Platform',
        value: dataFeeds.find((item) => item.id === platform)?.name ?? '-',
        editable: !isRenew && !isCreatePaAccount,
        editUrl: '/select-trading-platform',
      },
      {
        label: 'Account Type & Size',
        value: account
          ? `${accountType} | ${convertCurrencyToShortForm(account?.display_name_web)}`
          : '-',
        editable: !isCreatePaAccount,
        editUrl: `${isRenew ? '/renew' : ''}/select-account`,
      },
      {
        label: 'Add-Ons',
        value: selectedAddons.length ? selectedAddons : '-',
        editable: !isCreatePaAccount,
        editUrl: `${isRenew ? '/renew' : ''}/select-account`,
      },
      {
        label: 'Taxes & Processing Fee',
        value: taxes ? `$${formatCurrency(taxes)}` : '-',
        editable: false,
      },
    ],
    [
      account,
      accountType,
      platform,
      selectedAddons,
      taxes,
      isRenew,
      isCreatePaAccount,
    ]
  );

  const paymentData = useMemo(() => {
    if (account?.selectedAddonIds?.length) {
      return paymentCheckboxData.filter(
        (item) => item.value !== PaymentGateway.PAYPAL
      );
    }

    return paymentCheckboxData;
  }, [account?.selectedAddonIds?.length]);

  const handleBlurCouponCode = useCallback(
    async (field: ControllerRenderProps<PaymentFormValues, 'coupon_code'>) => {
      field.onBlur();
      if (field.value) {
        const res = await validateCoupon(field.value);

        setIsValidCoupons(!!res.data?.is_valid);
      }
    },
    []
  );

  const handleChangeState = useCallback(
    async (
      field: ControllerRenderProps<PaymentFormValues, 'state'>,
      value: string
    ) => {
      field.onChange(value);
      const zipRange = states.find((item) => item.value === value)?.zipRange;

      setValue('zip_range', zipRange);
    },
    [setValue, states]
  );

  const renderCheckValidCoupon = useMemo(() => {
    if (isNil(isValidCoupons)) {
      return <></>;
    }
    if (isValidCoupons) {
      return <CheckIcon className={'size-4'} color={'#14CA74'} />;
    }

    return <XIcon className={'size-4'} color={'red'} />;
  }, [isValidCoupons]);

  return (
    <Card className="max-w-6xl bg-card m-auto p-4 lg:p-6">
      <CardHeader className="mb-5">
        <CardTitle className="text-center text-2xl">
          {isCreatePaAccount ? '1' : isRenew ? '2' : '3'}.{' '}
          {t('checkout.reviewCheckout')}
        </CardTitle>
      </CardHeader>
      <CardContent className="bg-card-300 rounded-2xl p-0">
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <div className="grid grid-cols-1 gap-4 lg:gap-6 lg:grid-cols-2">
            <div className="space-y-4 pt-5 pb-5 lg:space-y-6 lg:border-r lg:border-white lg:pl-8 lg:pt-10 lg:pb-10">
              <div className="space-y-5">
                {/* Order Details */}
                {orderDetails.map((item) => (
                  <React.Fragment key={item.label}>
                    <div className="flex items-center justify-between px-4 lg:pl-0 lg:pr-10">
                      <span>{item.label}</span>
                      <div className="flex items-center gap-2 lg:gap-5">
                        {typeof item.value === 'string' ? (
                          <span className="font-medium">{item.value}</span>
                        ) : (
                          <div className={'flex flex-col'}>
                            {item.value?.map((item) => (
                              <span key={item?.id}>
                                {item.display_name_web === 'Unlock Level 8'
                                  ? `${item.display_name_web} Special Promo`
                                  : item.display_name_web}
                              </span>
                            ))}
                          </div>
                        )}
                        {item.editable ? (
                          <Link to={item.editUrl ?? ''}>
                            <EditIcon className="w-6 text-primary" />
                          </Link>
                        ) : (
                          <EditIcon className="w-6 text-transparent " />
                        )}
                      </div>
                    </div>
                    <hr />
                  </React.Fragment>
                ))}

                {/* Payment Details */}
                <div className="flex items-center justify-between pl-4 lg:pl-0 pr-4 lg:pr-22">
                  <span className="text-[16px]">Add-on</span>
                  <span className="text-[16px]">
                    {addonPrice ? `$${formatCurrency(addonPrice)}` : '-'}
                  </span>
                </div>

                <div className="flex items-center justify-between pl-4 lg:pl-0 pr-4 lg:pr-22">
                  <span className="text-[16px]">Recurring:</span>
                  <span className="text-[16px]">
                    {accountPrice
                      ? `$${formatCurrency(accountPrice)}/${firstPeriodValue} ${firstPeriodUnit}`
                      : '-'}
                  </span>
                </div>

                <div className="flex items-center justify-between pl-4 lg:pl-0 pr-4 lg:pr-22">
                  <span className="text-[16px]">
                    {t('checkout.totalDueToday')}
                  </span>
                  <span className="text-[16px]">
                    {addonPrice && accountPrice
                      ? `$${formatCurrency(addonPrice + accountPrice)}`
                      : '-'}
                  </span>
                </div>

                <div className="flex items-center justify-between pl-4 lg:pl-0 pr-4 lg:pr-22">
                  <span className="text-[26px] font-semibold">
                    {t('checkout.total')}
                  </span>
                  <span className="text-[26px] font-semibold">
                    ${formatCurrency(taxes + addonPrice + accountPrice)}
                  </span>
                </div>
              </div>

              <hr />

              <div className="space-y-4 pl-4 lg:pl-0 pr-4 lg:pr-22">
                <div className="space-y-2">
                  <Label className="text-sm" htmlFor="coupon_code">
                    {t('checkout.form.enterCoupon')}
                  </Label>
                  <Controller
                    control={control}
                    name="coupon_code"
                    render={({ field }) => (
                      <div>
                        <Input
                          {...field}
                          endIcon={renderCheckValidCoupon}
                          id="coupon_code"
                          inputSize={'lg'}
                          placeholder={t(
                            'checkout.form.enterCouponPlaceholder'
                          )}
                          textSize={'sm'}
                          onBlur={() => handleBlurCouponCode(field)}
                          onChange={(event) => {
                            setIsValidCoupons(undefined);
                            field.onChange(event.target.value);
                          }}
                        />
                        {errors.coupon_code && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.coupon_code.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label
                      className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
                      htmlFor="first_name"
                    >
                      {t('checkout.form.firstName')}
                    </Label>
                    <Controller
                      control={control}
                      name="first_name"
                      render={({ field }) => (
                        <div>
                          <Input
                            id="first_name"
                            inputSize={'lg'}
                            placeholder={t('checkout.form.firstName')}
                            textSize={'sm'}
                            {...field}
                          />
                          {errors.first_name && (
                            <p className="mt-1 text-xs text-red-500">
                              {errors.first_name.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label
                      className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
                      htmlFor="last_name"
                    >
                      {t('checkout.form.lastName')}
                    </Label>
                    <Controller
                      control={control}
                      name="last_name"
                      render={({ field }) => (
                        <div>
                          <Input
                            id="last_name"
                            inputSize={'lg'}
                            placeholder={t('checkout.form.lastName')}
                            textSize={'sm'}
                            {...field}
                          />
                          {errors.last_name && (
                            <p className="mt-1 text-xs text-red-500">
                              {errors.last_name.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm" htmlFor="referral_source">
                    {t('checkout.form.hearAboutUs')}
                  </Label>
                  <Controller
                    control={control}
                    name="referral_source"
                    render={({ field }) => (
                      <div>
                        <Input
                          id="referral_source"
                          inputSize={'lg'}
                          placeholder={t(
                            'checkout.form.hearAboutUsPlaceholder'
                          )}
                          textSize={'sm'}
                          {...field}
                        />
                        {errors.referral_source && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.referral_source.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4 lg:space-y-6 pl-4 pb-8 lg:pb-0 lg:pl-0 pr-4 lg:pt-10 lg:pr-22">
              <div>
                <h3 className="mb-4 text-[26px] font-medium">
                  {t('checkout.paymentTerm')}
                </h3>

                <Controller
                  control={control}
                  name="payment_type"
                  render={({ field }) => (
                    <div className="space-y-4">
                      {paymentData.map((item) => (
                        <div
                          key={item.value}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            checked={field.value === item.value}
                            className="w-[22px] h-[22px] border-2 border-white"
                            id="stripe"
                            onCheckedChange={() => field.onChange(item.value)}
                          />
                          <Label
                            className="flex items-center gap-2 text-[16px] ml-3"
                            htmlFor="stripe"
                          >
                            <div className="flex items-center gap-3">
                              <img
                                alt="stripe-payment"
                                height={item.height}
                                src={item.url}
                                width={item.width}
                              />
                              <span>{item.label}</span>
                            </div>
                          </Label>
                        </div>
                      ))}
                      {errors.payment_type && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.payment_type.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>

              <div className="mt-11 space-y-5">
                <div className="space-y-2">
                  <Label
                    className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
                    htmlFor="street"
                  >
                    {t('checkout.form.street')}
                  </Label>
                  <Controller
                    control={control}
                    name="street"
                    render={({ field }) => (
                      <div>
                        <Input
                          id="street"
                          inputSize={'lg'}
                          placeholder={t('checkout.form.streetPlaceholder')}
                          textSize={'sm'}
                          {...field}
                        />
                        {errors.street && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.street.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
                    htmlFor="city"
                  >
                    {t('checkout.form.city')}
                  </Label>
                  <Controller
                    control={control}
                    name="city"
                    render={({ field }) => (
                      <div>
                        <Input
                          id="city"
                          inputSize={'lg'}
                          placeholder={t('checkout.form.cityPlaceholder')}
                          textSize={'sm'}
                          {...field}
                        />
                        {errors.city && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.city.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
                    htmlFor="country"
                  >
                    {t('checkout.form.country')}
                  </Label>
                  <Controller
                    control={control}
                    name="country"
                    render={({ field }) => (
                      <div>
                        <InputSelect
                          borderColor={'border-[#979797]'}
                          className={'rounded-[10px] h-[42px] text-sm'}
                          options={countries}
                          placeholder={t('checkout.form.cityPlaceholder')}
                          value={field.value}
                          onChange={(value) => {
                            field.onChange(value);
                            resetField('state');
                            resetField('zip_code');
                            resetField('zip_range');
                          }}
                        />
                        {errors.country && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.country.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
                <div
                  className={cn(
                    country !== 'US' ? 'hidden' : 'block',
                    'space-y-2'
                  )}
                >
                  <Label
                    className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
                    htmlFor="state"
                  >
                    {t('checkout.form.state')}
                  </Label>
                  <Controller
                    control={control}
                    name="state"
                    render={({ field }) => (
                      <div>
                        <InputSelect
                          borderColor={'border-[#979797]'}
                          className={'rounded-[10px] h-[42px] text-sm'}
                          options={states}
                          placeholder={t('checkout.form.statePlaceholder')}
                          value={field.value ?? ''}
                          onChange={(value) => handleChangeState(field, value)}
                        />
                        {errors.state && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.state.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div
                  className={cn(
                    country !== 'US' ? 'hidden' : 'block',
                    'space-y-2'
                  )}
                >
                  <Label
                    className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
                    htmlFor="zip_code"
                  >
                    {t('checkout.form.zipCode')}
                  </Label>
                  <Controller
                    control={control}
                    name="zip_code"
                    render={({ field }) => (
                      <div>
                        <Input
                          {...field}
                          id="zip_code"
                          inputSize={'lg'}
                          placeholder={t('checkout.form.zipCodePlaceholder')}
                          textSize={'sm'}
                          value={field.value ?? ''}
                        />
                        {errors.zip_code && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.zip_code.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
                    htmlFor="phone_number"
                  >
                    {t('checkout.form.phoneNumber')}
                  </Label>
                  <Controller
                    control={control}
                    name="phone_number"
                    render={({ field }) => (
                      <div>
                        <Input
                          id="phone_number"
                          inputSize={'lg'}
                          placeholder={t(
                            'checkout.form.phoneNumberPlaceholder'
                          )}
                          textSize={'sm'}
                          {...field}
                        />
                        {errors.phone_number && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.phone_number.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
          <hr />
          <div className="flex items-center justify-end py-8 gap-5 lg:gap-10 pr-4 lg:pr-22">
            <Button
              className="lg:min-w-[145px]"
              size={'lg'}
              variant={'outline'}
              onClick={onBack}
            >
              {t('common.form.goBack')}
            </Button>
            <Button
              className="lg:min-w-[145px]"
              loading={isLoading}
              size={'lg'}
              type={'submit'}
              variant={'gradient'}
            >
              {t('common.form.proceed')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
